import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import howItWorks from '../data/howItWorksContent.json';

// Kategori renk şeması (Tailwind purge için literal sınıflar — dinamik string YOK).
const CATEGORY_COLORS = {
  emerald: { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  sky: { bar: 'bg-sky-500', text: 'text-sky-600 dark:text-sky-400' },
  violet: { bar: 'bg-violet-500', text: 'text-violet-600 dark:text-violet-400' },
  amber: { bar: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  default: { bar: 'bg-primary', text: 'text-primary' }
};

// Chrome'suz "Nasıl Çalışır" içeriği. Hem public sayfa (Header/Footer) hem app içi
// (DashboardLayout kabuğu) kullanır → kullanıcı dışarı atılmaz.
// Akış: Giriş → Tek Döngü → BBTV'nin farkı → Yetenekler → Modüller → CTA.
// variant: 'public' (kayıt ol CTA) | 'app' (mutfağa dön CTA).
export default function HowItWorksContent({ variant = 'public' }) {
  const location = useLocation();
  const meta = howItWorks?.meta || {};
  const categories = useMemo(
    () => (Array.isArray(howItWorks?.categories) ? howItWorks.categories : []),
    []
  );

  // İlk grubun ilk modülü varsayılan açık (Mutfağım).
  const [openModuleKey, setOpenModuleKey] = useState(categories[0]?.items?.[0]?.key || '');
  const [pendingScrollKey, setPendingScrollKey] = useState('');

  // Landing kartından / yetenek bloğundan #anchor ile gelindiğinde: modülü aç + kaydır.
  useEffect(() => {
    const hash = (location.hash || '').replace('#', '');
    if (!hash) return;
    setOpenModuleKey(hash);
    setPendingScrollKey(hash);
  }, [location.hash]);

  useEffect(() => {
    if (!pendingScrollKey) return;
    const el = document.getElementById(pendingScrollKey);
    if (el) {
      window.requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      setPendingScrollKey('');
    }
  }, [pendingScrollKey]);

  const toggleModule = (key) => {
    setOpenModuleKey((prev) => (prev === key ? '' : key));
  };

  const renderModule = (mod) => {
    const isOpen = mod.key === openModuleKey;
    return (
      <article
        key={mod.key}
        id={mod.key}
        className="scroll-mt-28 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:scroll-mt-32"
      >
        <button
          type="button"
          onClick={() => toggleModule(mod.key)}
          className="group flex w-full items-start justify-between gap-4 p-6 text-left"
        >
          <div>
            <h3 className="font-display text-xl font-bold text-on-surface transition-colors group-hover:text-primary dark:text-white">
              {mod.title}
            </h3>
            {mod.summary ? (
              <p className="mt-2 text-sm leading-relaxed text-on-surface-variant dark:text-slate-300">
                {mod.summary}
              </p>
            ) : null}
          </div>
          <span
            className={`material-symbols-outlined shrink-0 transition-transform ${
              isOpen ? 'rotate-180 text-primary' : 'text-slate-400'
            }`}
          >
            expand_more
          </span>
        </button>

        {isOpen ? (
          <div className="space-y-5 border-t border-slate-100 px-6 pb-8 pt-6 dark:border-slate-800">
            {mod.intro ? (
              <p className="text-base leading-relaxed text-on-surface dark:text-slate-200">
                {mod.intro}
              </p>
            ) : null}
            {(Array.isArray(mod.sections) ? mod.sections : []).map((sec, secIndex) => (
              <div key={secIndex}>
                {sec.title ? (
                  <h4 className="mb-2 font-display text-base font-bold text-on-surface dark:text-white">
                    {sec.title}
                  </h4>
                ) : null}
                {sec.body ? (
                  <p className="text-base leading-relaxed text-on-surface-variant dark:text-slate-300">
                    {sec.body}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </article>
    );
  };

  return (
    <div className="mx-auto w-full max-w-4xl">
      {/* 1. Giriş — BBTV nedir */}
      <section className="mb-12 border-b border-slate-200 pb-8 dark:border-slate-800">
        <p className="font-label mb-3 text-xs font-bold uppercase tracking-[0.22em] text-primary">Nasıl Çalışır</p>
        <h1 className="font-display text-3xl font-bold leading-tight text-on-surface md:text-5xl dark:text-white">
          BBTV Nasıl Çalışır?
        </h1>
        {meta.tagline ? (
          <p className="mt-6 max-w-3xl font-display text-xl font-bold leading-snug text-on-surface md:text-2xl dark:text-white">
            {meta.tagline}
          </p>
        ) : null}
        {(Array.isArray(meta.intro) ? meta.intro : meta.intro ? [meta.intro] : []).map((para, i) => (
          <p key={i} className="mt-4 max-w-3xl text-base leading-relaxed text-on-surface-variant dark:text-slate-300">
            {para}
          </p>
        ))}
      </section>

      {/* 2. Tek döngü — sayfanın yıldızı, en üstte */}
      {meta.systemLoop ? (
        <section className="mb-10 rounded-2xl border border-primary/20 bg-primary/5 p-8 dark:border-primary/30 dark:bg-primary/10">
          <h2 className="font-display text-2xl font-bold text-on-surface dark:text-white">
            {meta.systemLoop.title}
          </h2>
          {meta.systemLoop.intro ? (
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-on-surface-variant dark:text-slate-300">
              {meta.systemLoop.intro}
            </p>
          ) : null}
          <ol className="mt-6 space-y-4">
            {(Array.isArray(meta.systemLoop.steps) ? meta.systemLoop.steps : []).map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                  {i + 1}
                </span>
                <p className="pt-1 text-base leading-relaxed text-on-surface dark:text-slate-200">
                  <span className="font-bold">{step.label}</span>
                  <span className="text-on-surface-variant dark:text-slate-300"> — {step.body}</span>
                </p>
              </li>
            ))}
          </ol>
          {meta.systemLoop.closing ? (
            <p className="mt-6 max-w-3xl text-base font-medium leading-relaxed text-on-surface dark:text-white">
              {meta.systemLoop.closing}
            </p>
          ) : null}

          {meta.systemLoop.example ? (
            <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50/60 p-6 dark:border-emerald-900/40 dark:bg-emerald-900/10">
              {meta.systemLoop.example.title ? (
                <h3 className="font-display text-lg font-bold text-on-surface dark:text-white">
                  {meta.systemLoop.example.title}
                </h3>
              ) : null}
              {meta.systemLoop.example.intro ? (
                <p className="mt-2 text-base leading-relaxed text-on-surface-variant dark:text-slate-300">
                  {meta.systemLoop.example.intro}
                </p>
              ) : null}
              <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-stretch">
                {(Array.isArray(meta.systemLoop.example.chain) ? meta.systemLoop.example.chain : []).map((node, i, arr) => (
                  <React.Fragment key={i}>
                    <div className="flex-1 rounded-lg border border-emerald-200 bg-white p-4 dark:border-emerald-900/40 dark:bg-slate-900">
                      <p className="font-display text-sm font-bold text-emerald-700 dark:text-emerald-300">{node.stage}</p>
                      <p className="mt-1 text-sm leading-relaxed text-on-surface-variant dark:text-slate-400">{node.note}</p>
                    </div>
                    {i < arr.length - 1 ? (
                      <div className="flex items-center justify-center text-emerald-500 md:px-1">
                        <span className="material-symbols-outlined rotate-90 md:rotate-0">arrow_forward</span>
                      </div>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
              {meta.systemLoop.example.closing ? (
                <p className="mt-5 text-base font-medium leading-relaxed text-on-surface dark:text-white">
                  {meta.systemLoop.example.closing}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {/* 3. BBTV'nin farkı — döngünün altında, mesajı çivileyen vurucu kutu */}
      {meta.difference ? (
        <section className="mb-14 rounded-2xl border-2 border-primary/30 bg-white p-8 dark:bg-slate-900">
          <h2 className="font-display text-2xl font-bold text-on-surface dark:text-white md:text-3xl">
            {meta.difference.title}
          </h2>
          <div className="mt-4 space-y-3">
            {(Array.isArray(meta.difference.body) ? meta.difference.body : []).map((para, i, arr) => (
              <p
                key={i}
                className={
                  i === arr.length - 1
                    ? 'text-lg font-bold leading-relaxed text-primary'
                    : 'text-base leading-relaxed text-on-surface-variant dark:text-slate-300'
                }
              >
                {para}
              </p>
            ))}
          </div>
        </section>
      ) : null}

      {/* 4. Yetenek özeti */}
      {meta.capabilities ? (
        <section className="mb-14">
          <h2 className="font-display text-2xl font-bold text-on-surface dark:text-white md:text-3xl">
            {meta.capabilities.title}
          </h2>
          {meta.capabilities.intro ? (
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-on-surface-variant dark:text-slate-300">
              {meta.capabilities.intro}
            </p>
          ) : null}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(Array.isArray(meta.capabilities.items) ? meta.capabilities.items : []).map((cap, i) => (
              <a
                key={i}
                href={cap.anchor ? `#${cap.anchor}` : undefined}
                className="group block rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-primary/40 hover:bg-primary/5 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-primary/40"
              >
                <h3 className="font-display text-base font-bold text-on-surface transition-colors group-hover:text-primary dark:text-white">
                  {cap.label}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-on-surface-variant dark:text-slate-400">
                  {cap.body}
                </p>
              </a>
            ))}
          </div>
          {meta.capabilities.bridge ? (
            <p className="mt-6 max-w-3xl text-base font-medium leading-relaxed text-on-surface dark:text-white">
              {meta.capabilities.bridge}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* 5. Modüller — renkli, belirgin kategori başlıklarıyla */}
      <div className="space-y-16">
        {categories.map((cat) => {
          const col = CATEGORY_COLORS[cat.color] || CATEGORY_COLORS.default;
          return (
            <section key={cat.key}>
              <div className="mb-6 flex items-center gap-3 border-b border-slate-200 pb-3 dark:border-slate-800">
                <span className={`h-7 w-1.5 rounded-full ${col.bar}`} />
                <h2 className={`font-display text-lg font-black uppercase tracking-[0.14em] ${col.text}`}>
                  {cat.name}
                </h2>
              </div>
              <div className="space-y-4">
                {(Array.isArray(cat.items) ? cat.items : []).map(renderModule)}
              </div>
            </section>
          );
        })}
      </div>

      {/* 6. CTA */}
      {variant === 'app' ? (
        <section className="mt-20 rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-900">
          <h2 className="font-display text-2xl font-bold text-on-surface dark:text-white md:text-3xl">
            Mutfağına dönelim mi?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-on-surface-variant dark:text-slate-300">
            Gördüklerini hemen deneyebilirsin; sistem stoğun ve günlük rutinin üzerinden çalışır.
          </p>
          <Link
            to="/my-kitchen"
            className="mt-8 inline-block rounded bg-primary px-8 py-3 text-sm font-bold uppercase tracking-wide text-white"
          >
            Mutfağıma Git
          </Link>
        </section>
      ) : (
        <section className="mt-20 border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-900">
          <h2 className="font-display text-2xl font-bold text-on-surface dark:text-white md:text-3xl">
            Mutfağını yönetmeye hazır mısın?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-on-surface-variant dark:text-slate-300">
            Stoğunu gir, BBTV gerisini stok, bütçe ve günlük rutin üzerinden planlasın.
          </p>
          <Link
            to="/kayit-ol"
            className="mt-8 inline-block rounded bg-primary px-8 py-3 text-sm font-bold uppercase tracking-wide text-white"
          >
            Kayıt Ol
          </Link>
        </section>
      )}
    </div>
  );
}
